import { Program, type GL } from "./gl";

export enum LineStyle {
  Solid = 0,
  /** Tracer dashes moving from start to end. */
  Tracer = 1,
  /** Static dashes. */
  Dashed = 2,
}

const FLOATS = 4;
const BYTES = 8; // r, g, b, a, width (px * 16), style, spare, spare

const VERTEX = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aCorner;   // x: 0..1 along, y: -1..1 across
layout(location = 1) in vec4 aEnds;     // x0, y0, x1, y1
layout(location = 2) in vec4 aColor;
layout(location = 3) in vec4 aExtra;    // width/16 (0..255 -> 0..16px), style
uniform vec2 uCenter;
uniform float uZoom;
uniform vec2 uViewport;
out vec4 vColor;
out float vAlong;
out float vAcross;
out float vLength;
flat out int vStyle;
void main() {
  vec2 a = (aEnds.xy - uCenter) * uZoom;
  vec2 b = (aEnds.zw - uCenter) * uZoom;
  vec2 dir = b - a;
  float len = max(length(dir), 1e-4);
  vec2 n = vec2(-dir.y, dir.x) / len;
  float width = aExtra.x * 255.0 / 16.0 + 1.0;
  vec2 p = mix(a, b, aCorner.x) + n * aCorner.y * width * 0.5;
  gl_Position = vec4(p.x / uViewport.x * 2.0, -p.y / uViewport.y * 2.0, 0.0, 1.0);
  vColor = aColor;
  vAlong = aCorner.x * len;
  vAcross = aCorner.y;
  vLength = len;
  vStyle = int(aExtra.y * 255.0 + 0.5);
}`;

const FRAGMENT = `#version 300 es
precision highp float;
in vec4 vColor;
in float vAlong;
in float vAcross;
in float vLength;
flat in int vStyle;
uniform float uTime;
out vec4 outColor;
void main() {
  float a = vColor.a * (1.0 - smoothstep(0.5, 1.0, abs(vAcross)));
  if (vStyle == 1) {
    float phase = fract((vAlong - uTime * 90.0) / 14.0);
    a *= smoothstep(0.0, 0.15, phase) * (1.0 - smoothstep(0.45, 0.6, phase));
    // Fade in from the shooter.
    a *= smoothstep(0.0, 8.0, vAlong);
  } else if (vStyle == 2) {
    a *= step(0.5, fract(vAlong / 8.0));
  }
  if (a < 0.01) discard;
  outColor = vec4(vColor.rgb, a);
}`;

/** Batches instanced line segments. */
export class Lines {
  private program: Program;
  private vao: WebGLVertexArrayObject;
  private floatBuf: WebGLBuffer;
  private byteBuf: WebGLBuffer;
  private floats = new Float32Array(FLOATS * 4096);
  private bytes = new Uint8Array(BYTES * 4096);
  count = 0;

  constructor(private gl: GL) {
    this.program = new Program(gl, VERTEX, FRAGMENT);
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    const quad = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, -1, 1, -1, 0, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.floatBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.floatBuf);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 4, gl.FLOAT, false, FLOATS * 4, 0);
    gl.vertexAttribDivisor(1, 1);
    this.byteBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.byteBuf);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 4, gl.UNSIGNED_BYTE, true, BYTES, 0);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.UNSIGNED_BYTE, true, BYTES, 4);
    gl.vertexAttribDivisor(3, 1);
    gl.bindVertexArray(null);
  }

  begin(): void {
    this.count = 0;
  }

  push(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    color: readonly [number, number, number],
    alpha: number,
    widthPx: number,
    style: LineStyle = LineStyle.Solid,
  ): void {
    if (this.count * FLOATS >= this.floats.length) {
      const f = new Float32Array(this.floats.length * 2);
      f.set(this.floats);
      this.floats = f;
      const b = new Uint8Array(this.bytes.length * 2);
      b.set(this.bytes);
      this.bytes = b;
    }
    const i = this.count++;
    this.floats.set([x0, y0, x1, y1], i * FLOATS);
    const b = this.bytes;
    b[i * BYTES] = color[0] * 255;
    b[i * BYTES + 1] = color[1] * 255;
    b[i * BYTES + 2] = color[2] * 255;
    b[i * BYTES + 3] = alpha * 255;
    b[i * BYTES + 4] = Math.min(255, Math.max(0, (widthPx - 1) * 16));
    b[i * BYTES + 5] = style;
  }

  draw(center: [number, number], zoom: number, viewport: [number, number], time: number): void {
    if (this.count === 0) return;
    const gl = this.gl;
    const p = this.program;
    p.use();
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.floatBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.floats.subarray(0, this.count * FLOATS), gl.STREAM_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.byteBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.bytes.subarray(0, this.count * BYTES), gl.STREAM_DRAW);
    gl.uniform2f(p.u("uCenter"), center[0], center[1]);
    gl.uniform1f(p.u("uZoom"), zoom);
    gl.uniform2f(p.u("uViewport"), viewport[0], viewport[1]);
    gl.uniform1f(p.u("uTime"), time);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.count);
    gl.bindVertexArray(null);
  }
}
